/*
 * Route State (Normalized)
 *
 * Purpose:
 * - Provide one internal FE data shape for vessels, voyages, routes, and waypoints.
 * - Avoid duplicated nested copies and make state updates deterministic.
 * - Support multiple data sources (legacy analysed routes, core waypoints) through adapters.
 *
 * Design:
 * - entities.*ById: single source of truth for metadata objects.
 * - relations.*IdsBy*: parent/child links (vessel -> voyages, voyage -> routes).
 * - waypointsByRouteId: ordered waypoint lists used to build render geometry in selectors.
 *
 * Notes:
 * - Functions are pure and return new objects (no in-place mutation of input state).
 * - This module is state-focused only (no map/geojson rendering logic here).
 */

function toId(value) {
  const id = Number(value);
  return Number.isFinite(id) ? id : null;
}

function uniqIds(ids) {
  return Array.from(new Set((ids ?? []).map(toId).filter((id) => id != null)));
}

function byNumberExtendedThenTimestamp(a, b) {
  const aNum = Number(a?.number ?? 0);
  const bNum = Number(b?.number ?? 0);
  if (aNum !== bNum) return aNum - bNum;
  const aExtended = Number(a?.extended_number ?? a?.extendedNumber ?? 0);
  const bExtended = Number(b?.extended_number ?? b?.extendedNumber ?? 0);
  if (aExtended !== bExtended) return aExtended - bExtended;
  return (
    new Date(a?.timestamp ?? 0).getTime() - new Date(b?.timestamp ?? 0).getTime()
  );
}

export function createEmptyNormalizedState() {
  return {
    entities: {
      vesselsById: {},
      voyagesById: {},
      routesById: {},
    },
    relations: {
      vesselIds: [],
      voyageIdsByVesselId: {},
      routeIdsByVoyageId: {},
    },
    waypointsByRouteId: {},
  };
}

// Minimal single-route fragment for the merge-based reducers; patches exactly
// the given fields (upsertRoutes would stamp voyageId as a side effect).
export function createRoutePatchState(routeId, patch) {
  const next = createEmptyNormalizedState();
  next.entities.routesById[routeId] = { id: routeId, ...patch };
  return next;
}

// ROUTE_REPLACED merges entities, so a plain empty fragment would keep a
// user-given draft name alive past its discard — null it explicitly.
export function createDraftDiscardState(routeId) {
  return createRoutePatchState(routeId, { name: null });
}

export function mergeNormalizedStates(...states) {
  // Empty fragment slices return the input unchanged so consumers memoised on
  // a slice (e.g. entities.routesById) don't churn on unrelated dispatches.
  const mergeById = (a = {}, b = {}) => {
    if (Object.keys(b).length === 0) return a;
    const out = { ...a };
    for (const [id, value] of Object.entries(b)) {
      out[id] = {
        ...(out[id] ?? {}),
        ...(value ?? {}),
      };
    }
    return out;
  };

  // Relations merge as UNION: partial fragments (waypoint/fleet loaders) may
  // carry a subset of a parent's children and must never shrink the full
  // list. Merge never deletes — removeRoute/removeVoyage are the only delete
  // path; ordering lives in selectors.
  const mergeRelationLists = (a = {}, b = {}) => {
    if (Object.keys(b).length === 0) return a;
    const out = { ...a };
    for (const [parentId, ids] of Object.entries(b)) {
      out[parentId] = uniqIds([...(out[parentId] ?? []), ...(ids ?? [])]);
    }
    return out;
  };

  return (states ?? []).filter(Boolean).reduce(
    (acc, state) => ({
      entities: {
        vesselsById: mergeById(
          acc.entities?.vesselsById ?? {},
          state.entities?.vesselsById ?? {},
        ),
        voyagesById: mergeById(
          acc.entities?.voyagesById ?? {},
          state.entities?.voyagesById ?? {},
        ),
        routesById: mergeById(
          acc.entities?.routesById ?? {},
          state.entities?.routesById ?? {},
        ),
      },
      relations: {
        vesselIds: uniqIds([
          ...(acc.relations?.vesselIds ?? []),
          ...(state.relations?.vesselIds ?? []),
        ]),
        voyageIdsByVesselId: mergeRelationLists(
          acc.relations?.voyageIdsByVesselId ?? {},
          state.relations?.voyageIdsByVesselId ?? {},
        ),
        routeIdsByVoyageId: mergeRelationLists(
          acc.relations?.routeIdsByVoyageId ?? {},
          state.relations?.routeIdsByVoyageId ?? {},
        ),
      },
      waypointsByRouteId: {
        ...(acc.waypointsByRouteId ?? {}),
        ...(state.waypointsByRouteId ?? {}),
      },
    }),
    createEmptyNormalizedState(),
  );
}

export function setVesselIds(state, vesselIds) {
  return {
    ...state,
    relations: {
      ...state.relations,
      vesselIds: uniqIds(vesselIds),
    },
  };
}

export function setVoyageRelationForVessel(state, vesselId, voyageIds) {
  const id = toId(vesselId);
  if (id == null) return state;
  return {
    ...state,
    relations: {
      ...state.relations,
      voyageIdsByVesselId: {
        ...state.relations.voyageIdsByVesselId,
        [id]: uniqIds(voyageIds),
      },
    },
  };
}

export function setRouteRelationForVoyage(state, voyageId, routeIds) {
  const id = toId(voyageId);
  if (id == null) return state;
  return {
    ...state,
    relations: {
      ...state.relations,
      routeIdsByVoyageId: {
        ...state.relations.routeIdsByVoyageId,
        [id]: uniqIds(routeIds),
      },
    },
  };
}

// Ids belonging to a parent: relation list UNION entity link — relations can
// lag behind (read-after-write), the entity link is authoritative either way.
export function childIdsOf(relationList, entitiesById, parentKey, parentId) {
  const ids = new Set((relationList ?? []).map(toId));
  for (const [id, entity] of Object.entries(entitiesById ?? {})) {
    if (toId(entity?.[parentKey]) === toId(parentId)) ids.add(toId(id));
  }
  return ids;
}

// Tree rows carry `type`, waypoint-loader fragments only `routeType` —
// classification must accept both merge sources.
export function isPlannedEntity(entity) {
  return (entity?.type ?? entity?.routeType) === "planned";
}

export function isActiveEntity(entity) {
  return entity?.state != null
    ? String(entity.state).toLowerCase() === "active"
    : entity?.active === true;
}

const withoutIdsInLists = (relationMap, idSet) => {
  const out = {};
  for (const [parentId, ids] of Object.entries(relationMap ?? {})) {
    out[parentId] = (ids ?? []).filter(
      (candidate) => !idSet.has(toId(candidate)),
    );
  }
  return out;
};

export function removeRoute(state, routeId) {
  const id = toId(routeId);
  if (id == null) return state;
  return removeRoutes(state, new Set([id]));
}

function removeRoutes(state, routeIdSet) {
  const routesById = { ...state.entities.routesById };
  const waypointsByRouteId = { ...state.waypointsByRouteId };
  for (const id of routeIdSet) {
    delete routesById[id];
    delete waypointsByRouteId[id];
  }

  return {
    ...state,
    entities: { ...state.entities, routesById },
    relations: {
      ...state.relations,
      routeIdsByVoyageId: withoutIdsInLists(
        state.relations.routeIdsByVoyageId,
        routeIdSet,
      ),
    },
    waypointsByRouteId,
  };
}

export function removeVoyage(state, voyageId) {
  const id = toId(voyageId);
  if (id == null) return state;

  // Drafts (negative ids) are owned by the planner hooks — never cascade.
  const routeIds = new Set(
    [
      ...childIdsOf(
        state.relations.routeIdsByVoyageId?.[id],
        state.entities.routesById,
        "voyageId",
        id,
      ),
    ].filter((routeId) => routeId > 0),
  );
  const next = removeRoutes(state, routeIds);

  const voyagesById = { ...next.entities.voyagesById };
  delete voyagesById[id];
  const routeIdsByVoyageId = { ...next.relations.routeIdsByVoyageId };
  delete routeIdsByVoyageId[id];

  return {
    ...next,
    entities: { ...next.entities, voyagesById },
    relations: {
      ...next.relations,
      voyageIdsByVesselId: withoutIdsInLists(
        next.relations.voyageIdsByVesselId,
        new Set([id]),
      ),
      routeIdsByVoyageId,
    },
  };
}

export function upsertVessels(state, vessels = []) {
  const next = {
    ...state,
    entities: {
      ...state.entities,
      vesselsById: { ...state.entities.vesselsById },
    },
  };

  vessels.forEach((vessel) => {
    const id = toId(vessel?.id);
    if (id == null) return;
    next.entities.vesselsById[id] = {
      ...(next.entities.vesselsById[id] ?? {}),
      ...vessel,
      id,
    };
  });

  return next;
}

export function upsertVoyages(state, voyages = []) {
  const next = {
    ...state,
    entities: {
      ...state.entities,
      voyagesById: { ...state.entities.voyagesById },
    },
    relations: {
      ...state.relations,
      voyageIdsByVesselId: { ...state.relations.voyageIdsByVesselId },
    },
  };

  voyages.forEach((voyage) => {
    const voyageId = toId(voyage?.id);
    const vesselId = toId(voyage?.vesselId);
    if (voyageId == null) return;

    next.entities.voyagesById[voyageId] = {
      ...(next.entities.voyagesById[voyageId] ?? {}),
      ...voyage,
      id: voyageId,
      vesselId,
    };

    if (vesselId != null) {
      const current = next.relations.voyageIdsByVesselId[vesselId] ?? [];
      next.relations.voyageIdsByVesselId[vesselId] = uniqIds([
        ...current,
        voyageId,
      ]);
    }
  });

  return next;
}

export function upsertRoutes(state, routes = []) {
  const next = {
    ...state,
    entities: {
      ...state.entities,
      routesById: { ...state.entities.routesById },
    },
    relations: {
      ...state.relations,
      routeIdsByVoyageId: { ...state.relations.routeIdsByVoyageId },
    },
  };

  routes.forEach((route) => {
    const routeId = toId(route?.id ?? route?.routeId);
    const voyageId = toId(route?.voyageId);
    if (routeId == null) return;
    const previous = next.entities.routesById[routeId] ?? {};
    const effectiveVoyageId = voyageId ?? toId(previous?.voyageId);

    next.entities.routesById[routeId] = {
      ...previous,
      ...route,
      id: routeId,
      voyageId: effectiveVoyageId,
    };

    if (effectiveVoyageId != null) {
      const current = next.relations.routeIdsByVoyageId[effectiveVoyageId] ?? [];
      next.relations.routeIdsByVoyageId[effectiveVoyageId] = uniqIds([
        ...current,
        routeId,
      ]);
    }
  });

  return next;
}

export function upsertWaypointsByRoute(state, waypoints = []) {
  const next = {
    ...state,
    waypointsByRouteId: { ...state.waypointsByRouteId },
  };

  const grouped = new Map();
  (waypoints ?? []).forEach((wp) => {
    const routeId = toId(wp?.routeId ?? wp?.route_id);
    if (routeId == null) return;
    if (!grouped.has(routeId)) grouped.set(routeId, []);
    grouped.get(routeId).push(wp);
  });

  grouped.forEach((wps, routeId) => {
    const current = next.waypointsByRouteId[routeId] ?? [];
    const byWpId = new Map();

    [...current, ...wps].forEach((wp) => {
      const id = wp?.id != null ? toId(wp.id) : null;
      const extended = wp?.extended_number ?? wp?.extendedNumber ?? 0;
      const key =
        id == null
          ? `n:${wp?.number}:${extended}:${wp?.timestamp ?? ""}`
          : `id:${id}`;
      byWpId.set(key, { ...wp, routeId });
    });

    next.waypointsByRouteId[routeId] = Array.from(byWpId.values()).sort(
      byNumberExtendedThenTimestamp,
    );
  });

  return next;
}
